import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  Stack,
  LinearProgress,
} from "@mui/material";
import {
  People,
  AccessTime,
  TrendingUp,
  CheckCircle,
  Error as ErrorIcon,
  Speed,
  Devices,
  BarChart,
  History,
  Lock,
  Refresh,
  OnlinePrediction,
  Timer,
  Assessment,
} from "@mui/icons-material";
import { useAuth } from "../../context/authContext";
import auditService from "../../api/auditService";
import { usePerformanceStats } from "../../hooks/usePerformanceStats";

/**
 * Página de Rendimiento y Auditoría Completa
 * 
 * SECCIÓN PRIVADA - Solo para administradores y supervisores
 * 
 * Muestra TODA la información disponible del sistema de auditoría:
 * - Sesiones activas y cerradas
 * - Tiempo de conexión por usuario
 * - Logs de auditoría recientes
 * - Estadísticas completas del sistema
 * - Historial de actividad
 */
export default function Rendimiento() {
  const { user, role } = useAuth();
  const { data: performanceData, loading: loadingPerf, error: errorPerf, refetch } = usePerformanceStats();
  
  const [activeSessions, setActiveSessions] = useState([]);
  const [sessionStats, setSessionStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditStats, setAuditStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Verificar permisos
  const hasAccess = role === "admin" || role === "supervisor";

  // Cargar todos los datos
  const fetchAllData = async () => {
    if (!hasAccess) return;
    
    try {
      setLoading(true);
      setError(null);

      const [sessionsRes, statsRes, logsRes, auditStatsRes] = await Promise.all([
        auditService.getActiveSessions(),
        auditService.getSessionStats(),
        auditService.getLogs({ limit: 50 }),
        auditService.getStats(),
      ]);

      setActiveSessions(sessionsRes.data || []);
      setSessionStats(statsRes.data || null);
      setAuditLogs(logsRes.data?.logs || []);
      setAuditStats(auditStatsRes.data || null);
    } catch (err) {
      console.error("Error al cargar datos de auditoría:", err);
      setError(err.response?.data?.message || "Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [hasAccess]);

  const handleRefresh = () => {
    fetchAllData();
    refetch();
  };

  // Formatear duración
  const formatDuration = (seconds) => {
    if (!seconds) return "0m";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // Formatear fecha
  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("es-MX", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Formatear acción
  const getActionColor = (action) => {
    const colors = {
      CREATE: "success",
      UPDATE: "info",
      DELETE: "error",
      READ: "default",
      LOGIN: "primary",
      LOGOUT: "secondary",
      SESSION_START: "primary",
      SESSION_END: "secondary",
    };
    return colors[action] || "default";
  };

  if (!hasAccess) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          p: 4,
        }}
      >
        <Lock sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
        <Typography variant="h5" gutterBottom fontWeight={600}>
          Acceso Restringido
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Esta sección es privada y solo está disponible para administradores.
        </Typography>
        <Chip label={`Tu rol: ${role}`} color="default" sx={{ mt: 2 }} />
      </Box>
    );
  }

  const isLoading = loading || loadingPerf;

  return (
    <Box sx={{ p: 3 }}>
      {/* Encabezado */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            📊 Rendimiento y Auditoría del Sistema
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Vista completa de toda la actividad, sesiones y métricas del sistema
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
          disabled={isLoading}
        >
          Actualizar Todo
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" p={8}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* ========================================
              SECCIÓN 1: KPIs PRINCIPALES
          ======================================== */}
          <Typography variant="h6" fontWeight={600} mb={2} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Assessment /> KPIs del Sistema
          </Typography>
          
          <Grid container spacing={2} mb={4}>
            {/* Sesiones Activas */}
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ bgcolor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white", borderRadius: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <OnlinePrediction sx={{ mr: 1 }} />
                    <Typography variant="body2" fontWeight={500}>
                      Sesiones Activas
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight={700}>
                    {activeSessions.length}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {sessionStats?.totalSessions || 0} sesiones totales
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Tiempo Promedio */}
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ bgcolor: "#f093fb", color: "white", borderRadius: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Timer sx={{ mr: 1 }} />
                    <Typography variant="body2" fontWeight={500}>
                      Tiempo Promedio
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight={700}>
                    {formatDuration(sessionStats?.avgDuration || 0)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Por sesión
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Acciones Totales */}
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ bgcolor: "#4facfe", color: "white", borderRadius: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <TrendingUp sx={{ mr: 1 }} />
                    <Typography variant="body2" fontWeight={500}>
                      Acciones (7 días)
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight={700}>
                    {performanceData?.actions?.last7Days || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {performanceData?.actions?.today || 0} hoy
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Tasa de Errores */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={0}
                sx={{
                  bgcolor: performanceData?.errors?.percentage < 5 ? "#43e97b" : "#fa709a",
                  color: "white",
                  borderRadius: 3,
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    {performanceData?.errors?.percentage < 5 ? (
                      <CheckCircle sx={{ mr: 1 }} />
                    ) : (
                      <ErrorIcon sx={{ mr: 1 }} />
                    )}
                    <Typography variant="body2" fontWeight={500}>
                      Tasa de Errores
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight={700}>
                    {performanceData?.errors?.percentage || 0}%
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {performanceData?.errors?.total || 0} errores
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* ========================================
              SECCIÓN 2: SESIONES ACTIVAS
          ======================================== */}
          <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
            <Box display="flex" alignItems="center" mb={2}>
              <Devices sx={{ mr: 1, color: "primary.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Sesiones Activas Ahora ({activeSessions.length})
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {activeSessions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                No hay sesiones activas en este momento
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Usuario</strong></TableCell>
                      <TableCell><strong>Rol</strong></TableCell>
                      <TableCell><strong>Inicio</strong></TableCell>
                      <TableCell><strong>Duración</strong></TableCell>
                      <TableCell><strong>Acciones</strong></TableCell>
                      <TableCell><strong>IP</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeSessions.map((session) => (
                      <TableRow key={session._id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                              {session.userName?.charAt(0) || "?"}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {session.userName || "Sin nombre"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {session.userEmail}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={session.userRole} size="small" color="primary" />
                        </TableCell>
                        <TableCell>{formatDate(session.loginTime)}</TableCell>
                        <TableCell>
                          <Chip
                            label={formatDuration(session.duration)}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{session.actionsPerformed || 0}</TableCell>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">
                            {session.ipAddress || "-"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* ========================================
              SECCIÓN 3: RANKING DE USUARIOS
          ======================================== */}
          <Grid container spacing={3} mb={3}>
            {/* Usuarios Más Activos */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "divider", height: "100%" }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <People sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="h6" fontWeight={600}>
                    Usuarios Más Activos (7 días)
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  {performanceData?.users?.topActive?.slice(0, 10).map((user, index) => (
                    <ListItem
                      key={user._id}
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        bgcolor: index === 0 ? "action.hover" : "transparent",
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: index === 0 ? "primary.main" : index === 1 ? "secondary.main" : "grey.400",
                            width: 36,
                            height: 36,
                          }}
                        >
                          {index + 1}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={user.userName || "Usuario sin nombre"}
                        secondary={user.userEmail}
                        primaryTypographyProps={{ fontWeight: index === 0 ? 600 : 500 }}
                      />
                      <Chip label={`${user.count} acciones`} size="small" color={index === 0 ? "primary" : "default"} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            {/* Usuarios con Más Tiempo Conectado */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "divider", height: "100%" }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <AccessTime sx={{ mr: 1, color: "success.main" }} />
                  <Typography variant="h6" fontWeight={600}>
                    Más Tiempo Conectado (7 días)
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  {performanceData?.users?.mostTimeOnline?.slice(0, 10).map((user, index) => (
                    <ListItem
                      key={user._id}
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        bgcolor: index === 0 ? "action.hover" : "transparent",
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: index === 0 ? "success.main" : index === 1 ? "info.main" : "grey.400",
                            width: 36,
                            height: 36,
                          }}
                        >
                          {index + 1}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={user.userName || "Usuario sin nombre"}
                        secondary={`${user.count} sesiones`}
                        primaryTypographyProps={{ fontWeight: index === 0 ? 600 : 500 }}
                      />
                      <Chip
                        label={formatDuration(user.totalDuration || 0)}
                        size="small"
                        color={index === 0 ? "success" : "default"}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>

          {/* ========================================
              SECCIÓN 4: ESTADÍSTICAS DE ACCIONES
          ======================================== */}
          <Grid container spacing={3} mb={3}>
            {/* Acciones por Tipo */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <BarChart sx={{ mr: 1, color: "warning.main" }} />
                  <Typography variant="h6" fontWeight={600}>
                    Acciones por Tipo (7 días)
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box>
                  {performanceData?.actions?.byType?.map((action) => {
                    const percentage = ((action.count / performanceData.actions.last7Days) * 100).toFixed(1);
                    return (
                      <Box key={action._id} sx={{ mb: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="body2" fontWeight={500}>
                            {action._id}
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {action.count} ({percentage}%)
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={parseFloat(percentage)}
                          sx={{ height: 8, borderRadius: 1 }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>

            {/* Recursos Más Accedidos */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Speed sx={{ mr: 1, color: "error.main" }} />
                  <Typography variant="h6" fontWeight={600}>
                    Recursos Más Accedidos (7 días)
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box>
                  {performanceData?.actions?.byResource?.map((resource) => {
                    const percentage = ((resource.count / performanceData.actions.last7Days) * 100).toFixed(1);
                    return (
                      <Box key={resource._id} sx={{ mb: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="body2" fontWeight={500}>
                            {resource._id}
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {resource.count} ({percentage}%)
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={parseFloat(percentage)}
                          color="error"
                          sx={{ height: 8, borderRadius: 1 }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* ========================================
              SECCIÓN 5: LOGS RECIENTES
          ======================================== */}
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
            <Box display="flex" alignItems="center" mb={2}>
              <History sx={{ mr: 1, color: "info.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Logs de Auditoría Recientes (últimos 50)
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {auditLogs.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                No hay logs disponibles
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Fecha/Hora</strong></TableCell>
                      <TableCell><strong>Usuario</strong></TableCell>
                      <TableCell><strong>Acción</strong></TableCell>
                      <TableCell><strong>Recurso</strong></TableCell>
                      <TableCell><strong>Estado</strong></TableCell>
                      <TableCell><strong>Duración</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditLogs.map((log) => (
                      <TableRow key={log._id} hover>
                        <TableCell>
                          <Typography variant="caption">{formatDate(log.createdAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {log.userName || "Sistema"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {log.userEmail}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={log.action} size="small" color={getActionColor(log.action)} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{log.resource}</Typography>
                          {log.resourceId && (
                            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                              {log.resourceId.substring(0, 8)}...
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.status}
                            size="small"
                            color={log.status === "success" ? "success" : "error"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{log.duration ? `${log.duration}ms` : "-"}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
}
